import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Empty, PageHead, STATUSES, StatusBadge, money, useApi } from "@/components/shop/common";
import NewWorkOrderDialog from "@/components/shop/NewWorkOrderDialog";
import { useAuth } from "@/context/AuthContext";

export default function WorkOrders() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [status, setStatus] = useState("");
  const [wos] = useApi(`/work-orders${status ? `?status=${status}` : ""}`);
  const open = params.get("new") === "1";
  const setOpen = (v) => setParams(v ? { new: "1" } : {});

  return (
    <>
      <PageHead eyebrow="Jobs" title="Work orders">
        {user?.shop_role !== "technician" && <Button onClick={() => setOpen(true)} className="rounded-sm hover:bg-orange-600 hover:text-white" data-testid="new-wo-btn">
          <Plus className="h-4 w-4 mr-1" /> New work order
        </Button>}
      </PageHead>
      <div className="flex flex-wrap gap-2 mb-6">
        {[{ key: "", label: "All" }, ...STATUSES].map((s) => (
          <button key={s.key} onClick={() => setStatus(s.key)} data-testid={`wo-filter-${s.key || "all"}`}
            className={`px-3 py-1.5 text-xs font-mono uppercase tracking-wider rounded-sm border transition-colors ${status === s.key ? "bg-primary border-primary text-white" : "border-border text-muted-foreground hover:text-white"}`}>
            {s.label}
          </button>
        ))}
      </div>
      {wos?.length === 0 && <Empty testid="wo-empty">No work orders here yet.</Empty>}
      {wos?.length > 0 && (
        <div className="border border-border rounded-sm bg-card/80 overflow-x-auto">
          <table className="w-full text-sm" data-testid="wo-table">
            <thead className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
              <tr className="border-b border-border">
                {["WO", "Vehicle", "Customer", "Complaint", "Tech", "Status", "Total"].map((h) => <th key={h} className="text-left p-3 font-normal">{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {wos.map((w) => (
                <tr key={w.id} className="border-b border-border last:border-0 hover:bg-secondary/40 transition-colors" data-testid={`wo-row-${w.id}`}>
                  <td className="p-3 font-mono text-primary"><Link to={`/app/work-orders/${w.id}`} data-testid={`wo-link-${w.id}`}>#{w.number}</Link></td>
                  <td className="p-3 font-semibold whitespace-nowrap">{w.vehicle_label}</td>
                  <td className="p-3 whitespace-nowrap">{w.customer?.name}</td>
                  <td className="p-3 text-muted-foreground max-w-xs truncate">{w.complaint}</td>
                  <td className="p-3 whitespace-nowrap">{w.technician?.name || "—"}</td>
                  <td className="p-3"><StatusBadge status={w.status} /></td>
                  <td className="p-3 font-display text-lg font-bold">{money(w.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <NewWorkOrderDialog open={open} onOpenChange={setOpen} presetCustomer={params.get("customer")} />
    </>
  );
}
